export interface OrderCustomer {
  name: string;
  cnpj: string;
  phone: string;
  email: string;
}

export interface OrderItem {
  product_id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  ambiente?: string;
}

export interface PaymentOption {
  label: string;
  installments: number;
  installment_value: number;
  total: number;
  note?: string;
}

export interface DeliveryInfo {
  city: string;
  fee: number;
  free: boolean;
  notes: string;
}

export interface AssemblyInfo {
  included: boolean;
  value: number;
  notes: string;
}

export interface ParsedOrder {
  customer: OrderCustomer;
  items: OrderItem[];
  payment_options?: PaymentOption[];
  payment_terms: string;
  delivery?: DeliveryInfo;
  assembly?: AssemblyInfo;
  notes: string;
  delivery_deadline?: string;
  salesperson?: string;
  empresa_slug?: string;
}

export interface Product {
  id: number;
  name: string;
  description: string | null;
  price: number;
  media: { url: string; type: string }[];
}

export const STATUS_LABELS: Record<string, string> = {
  negociacao: 'Em negociação',
  fechado: 'Fechado',
  pago: 'Pago',
  entregue: 'Entregue',
  cancelado: 'Cancelado',
};

export const STATUS_ORDER = ['negociacao', 'fechado', 'pago', 'entregue', 'cancelado'];

export interface OrderItemRow {
  id: number;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  final_price: number | null;
  subtotal: number | null;
}

export interface OrderRow {
  id: number;
  status: string;
  recipient: { name?: string; cnpj?: string; phone?: string; email?: string } | null;
  payment_terms: string | null;
  customer_notes: string | null;
  subtotal_items: number | null;
  delivery_deadline: string | null;
  salesperson: string | null;
  empresa_slug: string | null;
  quote_id: number | null;
  created_at: string;
  order_items: OrderItemRow[];
}

export interface DeliveryItem {
  order_item_id?: number;
  product_name: string;
  quantity: number;
}

export interface DeliveryRow {
  id: number;
  order_id: number;
  delivery_number: number;
  status: string;
  items: DeliveryItem[];
  delivered_at: string | null;
  delivery_date: string | null;
  remind_at: string | null;
  note: string | null;
  attachment_path: string | null;
  attachment_name: string | null;
  attachment_url?: string | null;
  created_at: string;
  updated_at: string;
}

export const DELIVERY_STATUS_LABELS: Record<string, string> = {
  pendente: 'Pendente',
  entregue: 'Entregue',
};

export interface QuoteItem {
  product_id: string | number;
  product_name: string;
  quantity: number;
  unit_price: number;
  ambiente?: string;
  closed_quantity?: number;
}

export interface QuoteRow {
  id: number;
  customer_name: string;
  customer_cnpj: string;
  customer_phone: string;
  customer_email: string;
  items: QuoteItem[];
  payment_options: PaymentOption[] | null;
  payment_terms: string | null;
  delivery_city: string | null;
  delivery_fee: number | null;
  delivery_free: boolean | null;
  assembly_included: boolean | null;
  assembly_value: number | null;
  notes: string | null;
  delivery_deadline: string | null;
  salesperson: string | null;
  empresa_slug: string | null;
  total: number | null;
  status: string;
  converted_order_id: number | null;
  created_at: string;
  updated_at: string;
}

export const QUOTE_STATUS_LABELS: Record<string, string> = {
  aberto: 'Aberto',
  enviado: 'Enviado',
  aprovado: 'Aprovado',
  recusado: 'Recusado',
  expirado: 'Expirado',
};

export const QUOTE_STATUS_ORDER = ['aberto', 'enviado', 'aprovado', 'recusado', 'expirado'];

export interface LeadRow {
  id: number;
  name: string;
  company: string | null;
  phone: string | null;
  email: string | null;
  source: string | null;
  stage: string;
  value: number | null;
  notes: string | null;
  salesperson: string | null;
  next_follow_up: string | null;
  created_at: string;
  updated_at: string;
}

export const LEAD_STAGES: { key: string; label: string; tone: string }[] = [
  { key: 'novo_contato', label: 'Novo contato', tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'orcamento_enviado', label: 'Orçamento enviado', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'em_negociacao', label: 'Em negociação', tone: 'bg-[#E8610A]/10 text-[#E8610A] border-[#E8610A]/20' },
  { key: 'ganho', label: 'Ganho', tone: 'bg-green-50 text-green-700 border-green-200' },
  { key: 'perdido', label: 'Perdido', tone: 'bg-red-50 text-red-600 border-red-200' },
];

export const LEAD_STAGE_LABELS: Record<string, string> = {
  novo_contato: 'Novo contato',
  orcamento_enviado: 'Orçamento enviado',
  em_negociacao: 'Em negociação',
  ganho: 'Ganho',
  perdido: 'Perdido',
};

export interface ActivityRow {
  id: number;
  lead_id: number;
  type: string;
  content: string;
  follow_up_at: string | null;
  done: boolean;
  created_at: string;
}

export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  nota: 'Nota',
  ligacao: 'Ligação',
  email: 'E-mail',
  whatsapp: 'WhatsApp',
  reuniao: 'Reunião',
  lembrete: 'Lembrete',
};

export const ACTIVITY_TYPE_ICONS: Record<string, string> = {
  nota: 'ri-sticky-note-line',
  ligacao: 'ri-phone-line',
  email: 'ri-mail-line',
  whatsapp: 'ri-whatsapp-line',
  reuniao: 'ri-team-line',
  lembrete: 'ri-alarm-line',
};

// ---------------- CRM (novo modelo) ----------------

export interface EmpresaRow {
  id: number;
  nome: string;
  cnpj: string | null;
  razao_social: string | null;
  cidade: string | null;
  uf: string | null;
  endereco: string | null;
  bairro: string | null;
  segmento: string | null;
  tipo: string | null;
  porte: string | null;
  site: string | null;
  instagram: string | null;
  linkedin: string | null;
  email_geral: string | null;
  fonte: string | null;
  url_fonte: string | null;
  resumo: string | null;
  distancia_km: number | null;
  valor_potencial: number | null;
  status: string;
  nivel_confianca: string | null;
  score: number;
  classificacao: string;
  lead_id: number | null;
  papeis: string[] | null;
  origem: string | null;
  indicado_por_empresa_id: number | null;
  created_at: string;
  contatos: ContatoRow[];
  oportunidades: OportunidadeRow[];
}

export interface ContatoRow {
  id: number;
  empresa_id: number;
  nome: string | null;
  cargo: string | null;
  departamento: string | null;
  email: string | null;
  telefone: string | null;
  whatsapp: string | null;
  linkedin: string | null;
  instagram: string | null;
  nivel_influencia: string | null;
  nivel_decisao: string | null;
  observacao: string | null;
  respondeu: boolean;
  qualidade: string | null;
}

export interface OportunidadeRow {
  id: number;
  empresa_id: number;
  titulo: string | null;
  valor_potencial: number | null;
  etapa: string;
  score: number;
  fonte: string | null;
  motivo_contato: string | null;
  proxima_acao: string | null;
  proxima_data: string | null;
  created_at: string;
  empresas?: EmpresaRow;
}

export interface AtividadeRow {
  id: number;
  empresa_id: number;
  oportunidade_id: number | null;
  contato_id: number | null;
  tipo: string;
  conteudo: string;
  canal: string | null;
  data: string | null;
  prioridade: string;
  done: boolean;
  follow_up_at: string | null;
  origem: string;
  created_at: string;
  empresas?: { nome: string | null };
}

export const CRM_ETAPAS: { key: string; label: string; tone: string }[] = [
  { key: 'novo', label: 'Novo', tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'pesquisar_contato', label: 'Pesquisar contato', tone: 'bg-slate-50 text-slate-600 border-slate-200' },
  { key: 'contato_iniciado', label: 'Contato iniciado', tone: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  { key: 'em_conversa', label: 'Em conversa', tone: 'bg-teal-50 text-teal-700 border-teal-200' },
  { key: 'reuniao', label: 'Reunião', tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { key: 'proposta', label: 'Proposta', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'negociacao', label: 'Negociação', tone: 'bg-[#E8610A]/10 text-[#E8610A] border-[#E8610A]/20' },
  { key: 'ganho', label: 'Ganho', tone: 'bg-green-50 text-green-700 border-green-200' },
  { key: 'perdido', label: 'Perdido', tone: 'bg-red-50 text-red-600 border-red-200' },
  { key: 'acompanhar', label: 'Acompanhar', tone: 'bg-gray-50 text-gray-600 border-gray-200' },
];

export const CRM_ETAPA_LABELS: Record<string, string> = Object.fromEntries(
  CRM_ETAPAS.map((e) => [e.key, e.label]),
);

export const CRM_ETAPA_TONES: Record<string, string> = Object.fromEntries(
  CRM_ETAPAS.map((e) => [e.key, e.tone]),
);

export const CRM_TIPO_LABELS: Record<string, string> = {
  publico: 'Público',
  privado: 'Privado',
};

export const CRM_TIPO_TONES: Record<string, string> = {
  publico: 'bg-slate-100 text-slate-700 border-slate-300',
  privado: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export const CRM_SCORE_TONES: Record<string, { label: string; tone: string; icon: string }> = {
  quente: { label: 'Quente', tone: 'bg-red-50 text-red-600 border-red-200', icon: 'ri-fire-line' },
  prioritario: { label: 'Prioritário', tone: 'bg-orange-50 text-orange-600 border-orange-200', icon: 'ri-rocket-2-line' },
  acompanhar: { label: 'Acompanhar', tone: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ri-time-line' },
  baixa: { label: 'Baixa prioridade', tone: 'bg-gray-50 text-gray-500 border-gray-200', icon: 'ri-circle-line' },
};

export const ATIVIDADE_TIPOS: Record<string, { label: string; icon: string }> = {
  nota: { label: 'Nota', icon: 'ri-sticky-note-line' },
  ligacao: { label: 'Ligação', icon: 'ri-phone-line' },
  email: { label: 'E-mail', icon: 'ri-mail-line' },
  whatsapp: { label: 'WhatsApp', icon: 'ri-whatsapp-line' },
  reuniao: { label: 'Reunião', icon: 'ri-team-line' },
  lembrete: { label: 'Lembrete', icon: 'ri-alarm-line' },
  tarefa: { label: 'Tarefa', icon: 'ri-checkbox-circle-line' },
};